import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, X, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function Recipients() {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ALEPHTAV');
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    fetchRecipients();
  }, []);

  const fetchRecipients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/recipients`);
      const data = await response.json();
      setRecipients(data);
    } catch (error) {
      console.error('Error fetching recipients:', error);
      toast.error('Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecipient = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/recipients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newEmail.trim(),
          company: activeTab,
          active: true
        })
      });

      if (response.ok) {
        toast.success('Recipient added successfully');
        setNewEmail('');
        fetchRecipients();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add recipient');
      }
    } catch (error) {
      toast.error('Error adding recipient');
    }
  };

  const handleDelete = async (recipientId) => {
    if (!window.confirm('Are you sure you want to remove this recipient?')) return;

    try {
      await fetch(`${API_URL}/api/recipients/${recipientId}`, { method: 'DELETE' });
      toast.success('Recipient removed');
      fetchRecipients();
    } catch (error) {
      toast.error('Failed to remove recipient');
    }
  };

  const handleToggle = async (recipientId) => {
    try {
      await fetch(`${API_URL}/api/recipients/${recipientId}/toggle`, { method: 'PUT' });
      fetchRecipients();
    } catch (error) {
      toast.error('Failed to update recipient');
    }
  };

  const alephtavRecipients = recipients.filter(r => r.company === 'ALEPHTAV');
  const mautechRecipients = recipients.filter(r => r.company === 'MAU_TECH');

  if (loading) {
    return <div className="loading-spinner" />;
  }

  const RecipientList = ({ items, company }) => (
    <div>
      {/* Add New Recipient Form */}
      <form onSubmit={handleAddRecipient} style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <Input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="Enter email address"
          style={{ flex: 1 }}
          data-testid={`add-recipient-input-${company.toLowerCase()}`}
        />
        <Button type="submit" data-testid={`add-recipient-button-${company.toLowerCase()}`}>
          <Plus size={16} />
          Add Recipient
        </Button>
      </form>

      {/* Recipients List */}
      {items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Mail size={28} />
          </div>
          <div className="empty-state-title">No recipients yet</div>
          <div className="empty-state-text">
            Add email addresses to receive tender digests for {company === 'ALEPHTAV' ? 'Alephtav' : 'Mau Tech'}.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((recipient) => (
            <div
              key={recipient._id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                background: recipient.active ? 'white' : 'hsl(var(--muted))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 8,
                opacity: recipient.active ? 1 : 0.6
              }}
              data-testid={`recipient-row-${recipient._id}`}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: 'hsl(var(--primary) / 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'hsl(var(--primary))'
                  }}
                >
                  <Mail size={16} />
                </div>
                <div>
                  <div style={{ fontWeight: 500 }}>{recipient.email}</div>
                  <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
                    {recipient.active ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Switch
                  checked={recipient.active}
                  onCheckedChange={() => handleToggle(recipient._id)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(recipient._id)}
                  style={{ color: 'hsl(var(--error))' }}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="recipients-page">
      <div className="section-header">
        <div>
          <h2 className="section-title">Email Recipients</h2>
          <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: 14, marginTop: 4 }}>
            Manage who receives daily tender digest emails
          </p>
        </div>
      </div>

      <Card>
        <CardContent style={{ padding: 24 }}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="tabs-list">
              <TabsTrigger
                value="ALEPHTAV"
                className={`tab-button ${activeTab === 'ALEPHTAV' ? 'active' : ''}`}
                data-testid="recipients-company-tab-alephtav"
              >
                Alephtav ({alephtavRecipients.length})
              </TabsTrigger>
              <TabsTrigger
                value="MAU_TECH"
                className={`tab-button ${activeTab === 'MAU_TECH' ? 'active' : ''}`}
                data-testid="recipients-company-tab-mautech"
              >
                Mau Tech ({mautechRecipients.length})
              </TabsTrigger>
            </TabsList>

            <div style={{ marginTop: 24 }}>
              <TabsContent value="ALEPHTAV">
                <RecipientList items={alephtavRecipients} company="ALEPHTAV" />
              </TabsContent>
              <TabsContent value="MAU_TECH">
                <RecipientList items={mautechRecipients} company="MAU_TECH" />
              </TabsContent>
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

export default Recipients;
